import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import { useIsAdmin } from '../lib/auth';
import { clearSwrCache, clearSwrCacheByPrefix, hasFreshSwrCache, swrLoad } from '../lib/swrCache';
import type { ExtractedEvent, EventSeries, Venue } from '../types';
import EventDetailModal from '../components/EventDetailModal';
import EventEditModal from '../components/EventEditModal';
import VenueEditModal from '../components/VenueEditModal';
import VenueStatusSelect from '../components/VenueStatusSelect';
import VenueSourceBadge from '../components/VenueSourceBadge';
import SubscribeButton from '../components/SubscribeButton';
import FavoriteButton from '../components/FavoriteButton';
import EventList, { EventDensityToggle, type EventListDensity } from '../components/EventList';
import { useEventRejection } from '../hooks/useEventRejection';
import { useRecalculateDate } from '../lib/useRecalculateDate';
import { formatCategory } from '../lib/venue-categories';
import { compareEventDates } from '../lib/eventDates';
import { presetWindow } from '../lib/eventFilters';

const DENSITY_STORAGE_KEY = 'mh:eventListDensity';

function loadStoredDensity(): EventListDensity {
  if (typeof window === 'undefined') return 'compact';
  const v = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return v === 'cards' ? 'cards' : 'compact';
}

export default function VenueDetailPage() {
  const isAdmin = useIsAdmin();
  const { venueId } = useParams();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [events, setEvents] = useState<ExtractedEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExtractedEvent | null>(null);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [editingEvent, setEditingEvent] = useState<ExtractedEvent | null>(null);
  const [adminVenues, setAdminVenues] = useState<Venue[]>([]);
  const [adminSeries, setAdminSeries] = useState<EventSeries[]>([]);
  const [density, setDensity] = useState<EventListDensity>(loadStoredDensity);
  const eventWindow = useMemo(() => {
    const window = presetWindow('all')!;
    const from = window.from.toISOString(), to = window.to.toISOString();
    return { from, to, key: `events:${from}|${to}` };
  }, []);

  // Shared in-app reject modal + API call (same flow used on Home / Map).
  // Optimistically prunes the event from local state and closes the detail
  // panel if it was open for the same event.
  const { promptReject: openRejectModal, modal: rejectModal } = useEventRejection({
    onRejected: (event) => {
      setEvents((curr) => (curr ? curr.filter((e) => e.rowKey !== event.rowKey) : curr));
      setSelected((s) => (s && s.rowKey === event.rowKey ? null : s));
    },
  });
  function promptHideEvent(event: ExtractedEvent): void {
    setSelected((s) => (s && s.rowKey === event.rowKey ? null : s));
    openRejectModal(event);
  }

  const updateDensity = (d: EventListDensity) => {
    setDensity(d);
    if (typeof window !== 'undefined') window.localStorage.setItem(DENSITY_STORAGE_KEY, d);
  };

  // Admin: pre-load the full venue list once so the EventEditModal's venue
  // picker has options when the user clicks Edit on an event card. Public
  // /api/venues/public is fine — both endpoints expose `rowKey` + `name`.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api.get<Venue[]>('/api/venues/public')
      .then((vs) => { if (!cancelled) setAdminVenues(vs); })
      .catch(() => { /* non-blocking */ });
    api.get<EventSeries[]>('/api/mgmt/series')
      .then((ss) => { if (!cancelled) setAdminSeries(ss); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (all) => {
        const hit = all.find((v) => v.rowKey === venueId) ?? null;
        setVenue(hit);
        // Pending / inactive venues created by the venue-identifier helper
        // aren't in the public list. Fall back to the admin listing so
        // admins can still review them via the same URL.
        if (!hit && isAdmin) {
          api.get<Venue[]>('/api/mgmt/venues')
            .then((mgmt) => {
              if (cancelled) return;
              setVenue(mgmt.find((v) => v.rowKey === venueId) ?? null);
            })
            .catch(() => { /* keep existing null */ });
        }
      },
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    const { from, to, key: eventsKey } = eventWindow;
    if (!hasFreshSwrCache(eventsKey)) setEvents(null);
    void swrLoad<ExtractedEvent[]>({
      key: eventsKey,
      fetcher: () => api.get<ExtractedEvent[]>(`/api/events/published?from=${from}&to=${to}`),
      onData: (data) => setEvents(data),
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, [venueId, eventWindow]);

  const venueEvents = useMemo(
    () => (events ?? []).filter((e) => e.venueId === venueId).sort(compareEventDates),
    [events, venueId]
  );

  // Admin: per-event re-extract the original raw post (e.g. correct a wrong
  // date). Polls the same `events:from|to` window the page already loaded.
  const { recalculatingIds, markingWrongIds, recalculateDate, markWrongDate } = useRecalculateDate(
    // `fresh=1` skips the server's 60s response cache so the recalc poll and the
    // mark-wrong reconcile observe the just-applied edit instead of a stale snapshot.
    () => api.get<ExtractedEvent[]>(`/api/events/published?from=${eventWindow.from}&to=${eventWindow.to}&fresh=1`),
    setEvents,
    eventWindow.key,
  );

  return (
    <>
      <p className="small mb-2"><Link to="/">← All events</Link></p>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {!venue && !error && <LoadingBlock label="Loading venue…" />}

      {venue && (
        <header className="mb-4">
          {isAdmin && venue.reviewStatus === 'pending' && (
            <div className="alert alert-warning d-flex flex-wrap gap-2 align-items-center" role="status">
              <span className="flex-grow-1">
                <strong>Pending review.</strong> This venue was auto-created by the venue
                identifier helper. It is hidden from the public site until approved.
              </span>
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={async () => {
                  try {
                    const updated = await api.patch<Venue>(
                      `/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}`,
                      { reviewStatus: 'approved', active: true }
                    );
                    setVenue(updated);
                    clearSwrCache('venues:public');
                    clearSwrCacheByPrefix('events:');
                  } catch (e) {
                    setError(String(e));
                  }
                }}
              >
                Approve
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-info"
                onClick={async () => {
                  try {
                    const updated = await api.patch<Venue>(
                      `/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}`,
                      { reviewStatus: 'dismissed', active: false }
                    );
                    setVenue(updated);
                    clearSwrCache('venues:public');
                    // Events from dismissed venues are still shown on the
                    // public list, so refresh those caches too.
                    clearSwrCacheByPrefix('events:');
                  } catch (e) {
                    setError(String(e));
                  }
                }}
                title="Hide this venue from the active venues list but keep its events visible"
              >
                Dismiss
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={async () => {
                  try {
                    const updated = await api.patch<Venue>(
                      `/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}`,
                      { reviewStatus: 'declined', active: false }
                    );
                    setVenue(updated);
                    clearSwrCache('venues:public');
                    clearSwrCacheByPrefix('events:');
                  } catch (e) {
                    setError(String(e));
                  }
                }}
              >
                Decline
              </button>
            </div>
          )}
          {isAdmin && venue.autoDeclined === true && (
            <div className="alert alert-danger d-flex flex-wrap gap-2 align-items-center" role="status">
              <span className="flex-grow-1">
                <strong>Auto-declined.</strong>{' '}
                {venue.autoDeclineReason || 'Classifier matched prior declined venues.'}
                {typeof venue.historyClassifierConfidence === 'number' && (
                  <> {' '}<span className="text-muted">(confidence {venue.historyClassifierConfidence}%{venue.autoDeclineSource ? `, ${venue.autoDeclineSource}` : ''})</span></>
                )}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={async () => {
                  try {
                    const res = await api.post<{ id: string; updated: number; venue?: Venue }>(`/api/mgmt/venues/${encodeURIComponent(venue.rowKey)}/reconsider`, {});
                    if (res?.venue) setVenue(res.venue);
                    clearSwrCache('venues:public');
                  } catch (e) {
                    setError(String(e));
                  }
                }}
                title="Reset to pending review so an admin can re-evaluate"
              >
                Reconsider
              </button>
            </div>
          )}
          <div className="d-flex align-items-start gap-2 flex-wrap">
            <h1 className="mb-1 flex-grow-1">{venue.name}</h1>
            {isAdmin && (
              <VenueStatusSelect
                venue={venue}
                size="sm"
                onSaved={(v) => setVenue(v)}
              />
            )}
            {isAdmin && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setEditingVenue(venue)}
                aria-label={`Edit ${venue.name}`}
                title="Edit venue"
              >
                <i className="bi bi-pencil-square me-1" aria-hidden="true" />
                Edit
              </button>
            )}
            <SubscribeButton target={{ kind: 'venue', id: venue.rowKey, name: venue.name }} />
            <FavoriteButton kind="venue" targetId={venue.rowKey} targetName={venue.name} />
          </div>
          <p className="text-muted mb-2">
            {venue.partitionKey}
            {venue.category && <> · {formatCategory(venue.category)}</>}
          </p>
          {venue.address && <p className="mb-2">{venue.address}</p>}
          <p className="small d-flex flex-wrap gap-3 mb-2 align-items-center">
            <VenueSourceBadge venue={venue} />
          </p>
          <p className="small d-flex flex-wrap gap-3 mb-0">
            {venue.website && <a href={venue.website} target="_blank" rel="noreferrer noopener">Website ↗</a>}
            {venue.instagramHandle && (
              <a href={`https://instagram.com/${venue.instagramHandle}`} target="_blank" rel="noreferrer noopener">
                @{venue.instagramHandle} ↗
              </a>
            )}
            {venue.facebookUrl && (
              <a href={venue.facebookUrl} target="_blank" rel="noreferrer noopener">Facebook ↗</a>
            )}
          </p>
        </header>
      )}

      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h2 className="h5 mb-0">Upcoming at {venue?.name ?? 'this venue'}</h2>
        {events !== null && venueEvents.length > 0 && (
          <EventDensityToggle density={density} onChange={updateDensity} />
        )}
      </div>

      {events === null && <p>Loading events…</p>}
      {events && venueEvents.length === 0 && (
        <div className="alert alert-info">No upcoming events for this venue yet.</div>
      )}

      {events && venueEvents.length > 0 && (
        <EventList
          items={venueEvents}
          density={density}
          groupByDate
          hideVenue
          isAdmin={isAdmin}
          onSelect={setSelected}
          onEdit={(e) => setEditingEvent(e)}
          onHide={isAdmin ? promptHideEvent : undefined}
          onRecalculateDate={isAdmin ? recalculateDate : undefined}
          recalculatingIds={recalculatingIds}
          onMarkWrongDate={isAdmin ? markWrongDate : undefined}
          markingWrongIds={markingWrongIds}
          ariaLabel="Upcoming events at this venue"
          cardColClassName="col-md-6"
        />
      )}

      <EventDetailModal
        event={selected}
        onClose={() => setSelected(null)}
        onEdit={isAdmin ? (e) => { setSelected(null); setEditingEvent(e); } : undefined}
        onReject={isAdmin ? promptHideEvent : undefined}
      />

      <VenueEditModal
        venue={editingVenue}
        onClose={() => setEditingVenue(null)}
        neighborhoodOptions={Array.from(new Set(adminVenues.map((v) => v.partitionKey).filter(Boolean)))}
        onSaved={(v) => {
          setVenue(v);
          // Public venue list (and any list page using its SWR cache) is now stale.
          clearSwrCache('venues:public');
        }}
      />

      <EventEditModal
        event={editingEvent}
        venues={adminVenues}
        seriesList={adminSeries}
        onClose={() => setEditingEvent(null)}
        onSaved={(saved) => {
          // Splice the updated event into local state so the card reflects the
          // change immediately without a refetch.
          setEvents((curr) => curr ? curr.map((e) => e.rowKey === saved.rowKey ? { ...e, ...saved } : e) : curr);
          // Public events feed is now stale; nuke every cached window so the
          // home page picks up the change on next visit.
          clearSwrCacheByPrefix('events:');
        }}
      />
      {rejectModal}
    </>
  );
}
